import React, { useRef } from 'react';
import { X, Printer, Download } from 'lucide-react';
import { ReceiptTicket } from './ReceiptTicket';
import type { ReceiptData } from '../../utils/receipt';
import { downloadReceiptPdf } from '../../utils/receipt';
import { getPrinterConfig } from '../../hooks/usePrinterConfig';
import { Modal } from '../ui';

interface ReceiptPreviewModalProps {
  data: ReceiptData | null;
  onClose: () => void;
}

export function ReceiptPreviewModal({ data, onClose }: ReceiptPreviewModalProps) {
  const ticketRef = useRef<HTMLDivElement>(null);
  const paperWidth = getPrinterConfig().paperWidth;

  if (!data) return null;

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    downloadReceiptPdf(data);
  };

  return (
    <>
      {/* Print-only styles: hide everything except the ticket */}
      <style>{`
        @media print {
          @page { size: ${paperWidth}mm auto; margin: 0; }
          html, body { width: ${paperWidth}mm !important; margin: 0 !important; padding: 0 !important; }
          body > * { visibility: hidden !important; }
          #receipt-preview-print, #receipt-preview-print * { visibility: visible !important; }
          #receipt-preview-print {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: ${paperWidth}mm !important;
            box-shadow: none !important;
            border: none !important;
            z-index: 99999 !important;
          }
        }
      `}</style>

      <Modal open={!!data} onClose={onClose} titleId="receipt-preview-title" className="max-w-sm">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 id="receipt-preview-title" className="text-base font-semibold text-text">
            Vista previa del ticket
          </h2>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted/50 transition"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        </div>

        {/* Ticket preview */}
        <div className="overflow-y-auto max-h-[60vh] px-4 py-4 flex justify-center bg-gray-100">
          <div
            id="receipt-preview-print"
            className="shadow-lg"
            style={{ background: 'white', border: '1px solid #ddd' }}
          >
            <ReceiptTicket ref={ticketRef} data={data} />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 border-t border-border px-5 py-4">
          <button
            onClick={handlePrint}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 transition"
          >
            <Printer size={16} /> Imprimir
          </button>
          <button
            onClick={handleDownload}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-border px-4 py-2.5 text-sm font-semibold text-text hover:bg-muted/30 transition"
          >
            <Download size={16} /> Descargar PDF
          </button>
          <button
            onClick={onClose}
            className="flex items-center justify-center rounded-xl border border-border px-4 py-2.5 text-sm font-medium text-text-muted hover:bg-muted/30 transition"
          >
            Cerrar
          </button>
        </div>
      </Modal>
    </>
  );
}

export default ReceiptPreviewModal;
